import React from "react";
import { Helmet } from "react-helmet";

export interface SEOType {
  metaTitle: string;
  metaUrl: string;
  metaAuthor?: string;
  metaKeywords: string[];
  metaDescription: string;
  metaImage?: string;
}

interface SEOProps {
  contentfulSeo: {
    metaDescription: string;
    metaTitle: string;
    metaUrl: string;
    metaAuthor?: string;
    metaKeywords: string[];
    metaImage?: string;
  };
  breadcrumbRoutes?: {
    name: string;
    path: string;
  }[];
}

const SITE_URL = "https://www.zartek.in";

const humanizePathSegment = (segment: string): string =>
  decodeURIComponent(segment)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getBreadcrumbName = (segment: string, metaTitle: string, isLast: boolean): string => {
  if (isLast) {
    const titleWithoutBrand = metaTitle.split("|")[0]?.trim();

    if (titleWithoutBrand) {
      return titleWithoutBrand;
    }
  }

  return humanizePathSegment(segment);
};

const toAbsoluteUrl = (path: string, siteUrl: string): string => new URL(path, siteUrl).toString();

const buildBreadcrumbList = (metaUrl: string, metaTitle: string): any[] => {
  const pageUrl = new URL(metaUrl, SITE_URL);
  const pathname = pageUrl.pathname.replace(/\/+$/, "") || "/";

  if (pathname === "/") {
    return [];
  }

  const segments = pathname.split("/").filter(Boolean);
  const itemListElement = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${pageUrl.origin}/`,
    },
  ];

  segments.forEach((segment, index) => {
    const cumulativePath = `/${segments.slice(0, index + 1).join("/")}/`;

    itemListElement.push({
      "@type": "ListItem",
      position: index + 2,
      name: getBreadcrumbName(segment, metaTitle, index === segments.length - 1),
      item: new URL(cumulativePath, pageUrl.origin).toString(),
    });
  });

  return itemListElement;
};

const buildCustomBreadcrumbList = (breadcrumbRoutes: { name: string; path: string }[]): any[] => {
  return breadcrumbRoutes.map(({ name, path }, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name,
    item: toAbsoluteUrl(path, SITE_URL),
  }));
};

export default function SEO({
  contentfulSeo,
  breadcrumbRoutes,
}: SEOProps): React.ReactElement | null {
  if (!contentfulSeo) {
    return null;
  }

  const { metaDescription, metaKeywords, metaTitle, metaUrl, metaAuthor, metaImage } =
    contentfulSeo;
  const breadcrumbList = breadcrumbRoutes?.length
    ? buildCustomBreadcrumbList(breadcrumbRoutes)
    : buildBreadcrumbList(metaUrl, metaTitle);

  return (
    // constants for holding metadata!
    <>
      <Helmet
        title={metaTitle}
        meta={[
          {
            name: "description",
            content: metaDescription,
          },
          {
            name: "robots",
            content: "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
          },
          // Open Graph data
          {
            property: "og:locale",
            content: "en_US",
          },
          {
            property: "og:title",
            content: metaTitle,
          },
          {
            property: "og:description",
            content: metaDescription,
          },
          {
            property: "og:type",
            content: "website",
          },
          {
            name: "image",
            property: "og:image",
            content: metaImage || "https://zartek.in/icons/icon-48x48.png",
          },
          {
            property: "og:image:secure_url",
            content: metaImage || "https://zartek.in/icons/icon-48x48.png",
          },

          {
            property: "og:url",
            content: metaUrl,
          },

          // Twitter Card data
          {
            property: "twitter:card",
            content: "summary_large_image",
          },
          {
            property: "twitter:title",
            content: metaTitle,
          },
          {
            property: "twitter:description",
            content: metaDescription,
          },
          {
            property: "twitter:creator",
            content: metaAuthor || "@Zartek",
          },
          {
            name: "author",
            content: metaAuthor || "@Zartek",
          },
          {
            property: "twitter:image",
            content: metaImage || "https://zartek-india-v2.netlify.app/icons/icon-48x48.png",
          },
          { name: "twitter:site", content: "@zartekq" },
          // Google+
          {
            itemprop: "name",
            content: metaTitle,
          },
          {
            itemprop: "description",
            content: metaDescription,
          },
          {
            itemprop: "image",
            content: metaImage || "https://zartek-india-v2.netlify.app/icons/icon-48x48.png",
          },
        ].concat(
          metaKeywords && metaKeywords.length > 0
            ? {
                name: "keywords",
                content: metaKeywords.join(", "),
              }
            : [],
        )}
      />
      {breadcrumbList.length > 0 ? (
        <Helmet
          script={[
            {
              type: "application/ld+json",
              innerHTML: JSON.stringify(
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: breadcrumbList,
                },
                null,
                2,
              ),
            },
          ]}
        />
      ) : null}
    </>
  );
}
